import { StepReasoningEffort } from '@/types/automationSteps.ts';
import { LlmModelOption, ReasoningEffortId } from '@/types/llmModels.ts';

export const DEFAULT_REASONING_VALUE = StepReasoningEffort.Default;

export const REASONING_LABELS: Record<string, string> = {
  [StepReasoningEffort.Default]: 'Default',
  [StepReasoningEffort.NoReasoning]: 'None',
  [StepReasoningEffort.Minimal]: 'Minimal (unsupported — select a supported value)',
  [StepReasoningEffort.Low]: 'Low',
  [StepReasoningEffort.Medium]: 'Medium',
  [StepReasoningEffort.High]: 'High',
  [StepReasoningEffort.XHigh]: 'X High',
};

export const REASONING_LEVEL_OPTIONS = [
  { value: StepReasoningEffort.Default, label: REASONING_LABELS[StepReasoningEffort.Default] },
  {
    value: StepReasoningEffort.NoReasoning,
    label: REASONING_LABELS[StepReasoningEffort.NoReasoning],
  },
  { value: StepReasoningEffort.Low, label: REASONING_LABELS[StepReasoningEffort.Low] },
  { value: StepReasoningEffort.Medium, label: REASONING_LABELS[StepReasoningEffort.Medium] },
  { value: StepReasoningEffort.High, label: REASONING_LABELS[StepReasoningEffort.High] },
  { value: StepReasoningEffort.XHigh, label: REASONING_LABELS[StepReasoningEffort.XHigh] },
];

type ReasoningOption = {
  value: ReasoningEffortId;
  label: string;
  disabled?: boolean;
};

const withDeprecatedCurrentValue = (
  options: ReasoningOption[],
  currentValue: ReasoningEffortId,
): ReasoningOption[] => {
  if (currentValue !== StepReasoningEffort.Minimal) {
    return options;
  }

  return [
    ...options,
    {
      value: StepReasoningEffort.Minimal,
      label: REASONING_LABELS[StepReasoningEffort.Minimal],
      disabled: true,
    },
  ];
};

export const buildStepReasoningOptions = (currentValue: ReasoningEffortId): ReasoningOption[] =>
  withDeprecatedCurrentValue(REASONING_LEVEL_OPTIONS, currentValue);

export const buildModelReasoningOptions = (
  model: LlmModelOption | undefined,
  currentValue: ReasoningEffortId,
) => {
  const values = new Set<ReasoningEffortId>([DEFAULT_REASONING_VALUE]);

  model?.allowed_reasoning_efforts.forEach((value) => {
    if (value !== StepReasoningEffort.Minimal) {
      values.add(value);
    }
  });

  if (currentValue && currentValue !== StepReasoningEffort.Minimal) {
    values.add(currentValue);
  }

  const options = Array.from(values).map((value) => ({
    value,
    label: REASONING_LABELS[value] ?? value,
  }));

  return withDeprecatedCurrentValue(options, currentValue);
};

export const getReasoningSelectValue = (value: ReasoningEffortId): ReasoningEffortId => {
  return value || DEFAULT_REASONING_VALUE;
};

export const getAutomationReasoningUpdateValue = (value: string | null): ReasoningEffortId => {
  return value === DEFAULT_REASONING_VALUE || value === null ? '' : value;
};
